import {
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Inject,
    Input,
    OnInit,
    Optional,
    Output,
    ViewChild,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
} from '@angular/forms';
import {
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
    CmpsDialogRef,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { filter, pairwise, startWith, tap } from 'rxjs/operators';

import {
    FocusSelectionParams,
    InteractionEntity,
} from '../../coaching-base/config';
import {
    CommitmentFocusSummaryDto,
    FocusDto,
    FocusMetricDto,
    NoteCategory,
} from '../../coaching-base/general';
import { CoachingSwitchboardService } from '../../coaching-base/switchboard';
import { tagToTagItem } from '../../coaching-commitment/functions';
import { BehaviourItem } from '../../coaching-tags/models/behaviour.coaching.model';
import { MetricItem } from '../../coaching-tags/models/metric.coaching.model';
import { SubBehaviourItem } from '../../coaching-tags/models/subbehaviour.coaching.model';
import { TagItem } from '../../coaching-tags/models/tag.coaching.model';
import {
    composeTagIdentifierNext,
    TagSetMode,
} from '../../coaching-tags/tags/tags.model';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { TranslationKeyMap } from '../coaching-observation.i18n';
import { CmpsForm, CmpsFormBuilder } from '../experimental.form';
import { ObservationFormDecorateConnector } from '../form-decorator/observation-form-decorate-connector';
import { ObservationFormDecoratorService } from '../form-decorator/observation-form-decorator.service';
import { ObservationCommitmentUpsertEvent } from '../observation-commitment-upsert-event';
import { InteractionsObservationComponent } from '../observation-interactions/interactions.observation.component';
import {
    ObservationMenuItem,
    ObservationMenuItemType,
} from '../observation-menu-item';
import { ObservationExtraOptions } from '../observation/observation-extra-options.model';
import {
    AllowedActions,
    ObservationFormDisplayProperties,
    READONLY_OBSERVATION,
    ReadonlyObservation,
} from '../observation/observation.model';
import { FocusContainerValidationResult } from '../observation/validation/focus-container-validation-result.model';

import { TagGroupFormValue } from './tag-group-form-value.model';
import { TagGroupFormFields } from './tag-group.observation.form';

@Component({
    selector: '[cmpsObservationTagGroup]',
    templateUrl: './tag-group.observation.component.html',
    host: {
        '[class.ontp-drafted-observation-group]':
            '!published && _value.behaviourId != null && !_hasActiveCommitment',
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TagGroupObservationComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => TagGroupObservationComponent),
            multi: true,
        },
    ],
})
@TakeUntilDestroy()
export class TagGroupObservationComponent
    implements
        OnInit,
        ControlValueAccessor,
        Validator,
        TakeUntilDestroy,
        ObservationFormDecorateConnector
{
    @Input() storePathPrefix: string;

    @ViewChild(InteractionsObservationComponent)
    readonly interactions: InteractionsObservationComponent;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _TagSetMode = TagSetMode;
    /** @private */ _TagGroupFormFields = TagGroupFormFields;
    @Input() observationId: number;
    @Input() published: boolean;
    @Input() displayProperties: ObservationFormDisplayProperties;
    @Input() performerId: number;
    @Input() focusSelectionParams: FocusSelectionParams;
    @Input() interactionEntity: InteractionEntity;
    @Input() defaultInteractionTypeId: number;
    @Input() defaultNoteCategory: NoteCategory;
    @Input() allowedActions: AllowedActions;
    @Input() behaviours: BehaviourItem[];
    @Input() subBehaviours: SubBehaviourItem[];
    @Input() metrics: MetricItem[];
    @Input() isCollapsed: boolean;
    @Input() isGeneric: boolean;
    @Input() activeMetric: number;
    @Output() isCollapsedChange: EventEmitter<boolean>;
    @Output() selectFocus: EventEmitter<number /* trackingId */>;
    @Output() copyNotes: EventEmitter<number /* trackingId */>;
    @Output() upsertCommitment: EventEmitter<ObservationCommitmentUpsertEvent>;
    @Input() extraOptions: ObservationExtraOptions;
    /** @private */ _form: CmpsForm<TagGroupFormFields>;
    private _blackout: boolean;
    private _initialValue: TagGroupFormValue;
    /** @private */ _focusInfo: TagItem;
    /** @private */ _matchesFocus: boolean;
    /** @private */ _isLinked: boolean;
    /** @private */ _hasActiveCommitment: boolean;
    /** @private */ _menuOpts: ObservationMenuItem[];
    /** @private */ _kpiSelectedStr: string;
    /** @private */ _selected: FocusDto;
    /** @private */ _pinnedMetrics: number[];
    private _onChange: (_: TagGroupFormValue) => void;
    private _onTouched: (_: any) => void;
    private _onValidatorChange: (_: any) => void;
    private _onRemove: () => void;

    /** @private */ get _value(): TagGroupFormValue {
        return this._form.form.value;
    }

    constructor(
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _translateService: TranslateService,
        private readonly _dialog: CmpsDialog,
        private readonly _formBuilder: CmpsFormBuilder,
        private readonly _switchboardService: CoachingSwitchboardService,
        private readonly _formDecoratorService: ObservationFormDecoratorService,
        /** @private */ @Optional()
        readonly _dialogRef: CmpsDialogRef<any, any>,
        @Optional()
        @Inject(READONLY_OBSERVATION) /** @private */
        readonly _readonly: ReadonlyObservation,
    ) {
        this.isCollapsedChange = new EventEmitter();
        this.selectFocus = new EventEmitter();
        this.copyNotes = new EventEmitter();
        this.upsertCommitment = new EventEmitter();
    }

    ngOnInit(): void {
        this._form =
            this._formBuilder.create<TagGroupFormFields>(TagGroupFormFields);

        this._form.form.valueChanges
            .pipe(
                filter(() => !this._blackout),
                startWith(null as TagGroupFormValue),
                pairwise(),
                tap<[TagGroupFormValue, TagGroupFormValue]>(([prev, value]) => {
                    /* 'cause of the side effects of things that touch the form, change tracking is done like this */
                    prev = prev || this._initialValue;
                    const next: TagGroupFormValue = {
                        ...value,
                        trackingChanged:
                            prev.trackingChanged ||
                            value.trackingChanged ||
                            prev.focusId !== value.focusId ||
                            prev.userId !== value.userId ||
                            prev.behaviourId !== value.behaviourId ||
                            prev.subBehaviourId !== value.subBehaviourId ||
                            (!prev.metrics ? null : prev.metrics.join(',')) !==
                                (!value.metrics
                                    ? null
                                    : value.metrics.join(',')) || // TODO revise
                            value.interactions.some(
                                (e) => e.trackingChanged,
                            ) /* TODO revise should add/remove interaction cause trackingChanged */,
                    };

                    this._form.form.patchValue(
                        {
                            trackingChanged: next.trackingChanged,
                        },
                        { emitEvent: false },
                    );

                    CMPS_APP_DEF.ENV !== 'prod' &&
                        console.log('_onChange tag-group', next);
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    this._onChange && this._onChange(next);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this._formDecoratorService.unRegister(this);
        this.isCollapsedChange.complete();
        this.selectFocus.complete();
        this.copyNotes.complete();
        this.upsertCommitment.complete();
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        isDisabled
            ? this._form.form.disable({ emitEvent: false })
            : this._form.form.enable({ emitEvent: false });

        this._determineMenuOptions();
    }

    sub: Subscription;
    token: number;

    writeValue(obj: TagGroupFormValue): void {
        this._patchForm(obj);
    }

    registerOnValidatorChange(fn: () => void): void {
        this._onValidatorChange = fn;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        this._form.form.updateValueAndValidity({
            onlySelf: true,
            emitEvent: false,
        });
        this._form.checkWarningsErrors();
        return this._form.form.errors;
    }

    fdcDecorate(result: FocusContainerValidationResult): void {
        const el = this._el.nativeElement.querySelector(
            `[id='${this._value.trackingId}']`,
        );
        if (!el) {
            return;
        }
        if (!result.focusSelected) {
            el.classList.add('outline_fc');
        } else {
            el.classList.remove('outline_fc');
        }
    }

    fdcDecorateDelta(
        result: FocusContainerValidationResult,
        deltaResult: FocusContainerValidationResult,
    ): void {
        this.fdcDecorate(deltaResult);
    }

    fdcUnDecorate(result: FocusContainerValidationResult): void {
        const el = this._el.nativeElement.querySelector(
            `[id='${this._value.trackingId}']`,
        );
        if (el) {
            el.classList.remove('outline_fc');
        }
    }

    addInteraction(): void {
        this.isCollapsed = false;
        this.interactions.addInteraction();
    }

    onRemove(fn: any): void {
        this._onRemove = fn;
    }

    /** @private */ _onMenuItemClick(item: ObservationMenuItem): void {
        let prom = Promise.resolve();
        switch (item.type) {
            case ObservationMenuItemType.SET_FOCUS:
                this.selectFocus.emit(this._value.trackingId);
                break;
            case ObservationMenuItemType.DELETE:
                this._discard();
                break;
            case ObservationMenuItemType.COPY_NOTES_TO_NEW_FOCUS:
                this.copyNotes.emit(this._value.trackingId);
                break;
            case ObservationMenuItemType.LINK:
                prom = this._switchboardService.commitment.sbLinkFocus(
                    this._value,
                    this.observationId,
                );
                // TODO bubble an event up to the parent instead of doing this here
                this._dialogRef && (this._dialogRef as any)._closePrivate();
                break;
            case ObservationMenuItemType.UNLINK:
                this._switchboardService.commitment.sbUnlinkFocus(
                    this._value,
                    this.observationId,
                );
                break;
            case ObservationMenuItemType.START_COMMITMENT:
            case ObservationMenuItemType.UPDATE_COMMITMENT:
                this._confirmUpsertCommitment();
                break;
            default:
                break;
        }
        // TODO do this here or have it trickle down by having commitment component trigger a method on the switchboard?
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        prom.catch(() => null).then(() => {
            this._determineMenuOptions();
        });
    }

    // yes. keep this explicit click handler
    /** @private */ _onAddInteractionClick(): void {
        this.addInteraction();
    }

    /** @private */ _onAddMetricClick(focus: FocusDto): void {
        if (!focus) {
            return;
        }
        const selectedMetrics = this._value.metrics.map((e) => e.metricId);
        const focusMetrics = focus.metrics
            .filter((e) => !selectedMetrics.includes(e))
            .map<FocusMetricDto>((e) => ({
                metricId: e,
                metricFullName: null,
                metricShortName: null,
                metricInitials: null,
                isActiveGoal: false,
            }));
        const next = this._value.metrics.concat(focusMetrics);
        this._form.form.patchValue({ [TagGroupFormFields.METRICS.name]: next });
        this._selected = {
            ...this._selected,
            metrics:
                next.filter((e) => !e.isActiveGoal).map((e) => e.metricId) ||
                [],
        };
    }

    /** @private */ _onRemoveMetricClick(metricId: number): void {
        const next = this._value.metrics.filter(
            (e) =>
                e.isActiveGoal || (!e.isActiveGoal && e.metricId !== metricId),
        );
        this._selected = {
            ...this._selected,
            metrics:
                next.filter((e) => !e.isActiveGoal).map((e) => e.metricId) ||
                [],
        };
        this._form.form.patchValue({ [TagGroupFormFields.METRICS.name]: next });
    }

    /** @private */ _onToggleCollapseClick(): void {
        this.isCollapsed = !this.isCollapsed;
        this.isCollapsedChange.emit(this.isCollapsed);
    }

    private _patchForm(value: TagGroupFormValue): void {
        this._blackout = true;
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        this._initialValue = value || ({} as TagGroupFormValue);
        this._form.clearWarningsErrors();
        this._form.form.reset(this._initialValue, { emitEvent: false });

        if (this._value.behaviourId != null) {
            this._focusInfo = tagToTagItem(
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                {
                    behaviourId: this._value.behaviourId,
                    subBehaviourId: this._value.subBehaviourId,
                } as CommitmentFocusSummaryDto,
                this.behaviours,
                this.subBehaviours,
            );
        } else {
            this._focusInfo = {
                id: -1,
                identifier: composeTagIdentifierNext(this._value),
                name: TranslationKeyMap._9622_BEHAVIOUR___SUB_BEHAVIOUR_FOCUS,
                tag: null,
                behaviourName: this._translateService.instant(
                    TranslationKeyMap._7334_BEHAVIOUR,
                ),
                subBehaviourName: this._translateService.instant(
                    TranslationKeyMap._7336_SUB_BEHAVIOUR,
                ),
                tooltip:
                    TranslationKeyMap._9622_BEHAVIOUR___SUB_BEHAVIOUR_FOCUS,
            };
        }

        this._determineMenuOptions();

        this._formDecoratorService.unRegister(this);
        this._formDecoratorService.registerForTrackingId(
            this._value.trackingId,
            this,
        );

        this._selected = {
            focusId: this._value.focusId,
            userId: this._value.userId,
            behaviourId: this._value.behaviourId,
            subBehaviourId: this._value.subBehaviourId,
            metrics: this._value.metrics
                ? this._value.metrics
                      .filter((e) => !e.isActiveGoal)
                      .map((e) => e.metricId)
                : [],
        };
        this._pinnedMetrics = [];
        if (!this.isGeneric && this.activeMetric != null) {
            this._pinnedMetrics.push(this.activeMetric);
        }
        this._blackout = false;
    }

    private _discard(): void {
        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._9663_ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_FOCUS_CONTAINER_,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9297_YES,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9298_NO,
                    ),
                    data: 1,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret === 0) {
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        this._onRemove && this._onRemove();
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _determineMenuOptions(): void {
        this._matchesFocus =
            this.published &&
            this._switchboardService.commitment.sbHasMatchingFocus(this._value);
        this._isLinked = false;
        this._hasActiveCommitment =
            this._switchboardService.pillarTwo.sbHasActiveCommitmentWithFocus(
                this._value,
            );

        if (this._matchesFocus) {
            this._isLinked =
                this._switchboardService.commitment.sbHasLinkedFocus(
                    this._value,
                    this.observationId,
                );
        }

        this._menuOpts = [];

        if (
            !this._form.form.disabled &&
            !this.published &&
            (!this._hasActiveCommitment ||
                this.extraOptions?.canChangeActiveFocus === true)
        ) {
            this._menuOpts.push({
                type: ObservationMenuItemType.SET_FOCUS,
                label: this._translateService.instant(
                    this._value.behaviourId == null
                        ? TranslationKeyMap._9658_SET_FOCUS
                        : TranslationKeyMap._9667_CHANGE_FOCUS,
                ),
                icon: 'image-filter-center-focus-strong',
            });
        }

        if (this._matchesFocus && !this._isLinked) {
            this._menuOpts.push({
                type: ObservationMenuItemType.LINK,
                label: this._translateService.instant(
                    TranslationKeyMap._9634_LINK,
                ),
                icon: 'link',
            });
        }

        if (this.published && !this._hasActiveCommitment) {
            this._menuOpts.push({
                type: ObservationMenuItemType.START_COMMITMENT,
                label: this._translateService.instant(
                    TranslationKeyMap._9825_START_COMMITMENT,
                ),
                icon: 'link',
            });
        }

        if (this.published && this._hasActiveCommitment) {
            this._menuOpts.push({
                type: ObservationMenuItemType.UPDATE_COMMITMENT,
                label: this._translateService.instant(
                    TranslationKeyMap._9668_UPDATE_COMMITMENT,
                ),
                icon: 'link',
            });
        }

        if (!this._form.form.disabled && !this.published) {
            this._menuOpts.push({
                type: ObservationMenuItemType.DELETE,
                label: this._translateService.instant(
                    TranslationKeyMap._7424_DELETE,
                ),
                icon: 'delete',
            });
        }
    }

    private _confirmUpsertCommitment(): void {
        const focus =
            this._switchboardService.pillarTwo.sbActiveCommitmentWithFocus(
                this._value,
            );
        const msg =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            focus && focus.commitmentId != null
                ? TranslationKeyMap._9675_DO_YOU_WANT_TO_EDIT_THE_COMMITMENT_
                : TranslationKeyMap._9556_DO_YOU_WANT_TO_CREATE_A_NEW_COMMITMENT_;

        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(msg),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9297_YES,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9298_NO,
                    ),
                    data: 1,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret === 0) {
                        this.upsertCommitment.emit({
                            trackingId: this._value.trackingId,
                            commitmentId: focus ? focus.commitmentId : null,
                            focus: {
                                behaviourId: this._value.behaviourId,
                                subBehaviourId: this._value.subBehaviourId,
                            },
                        });
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
